import React from 'react';
import { Volume2, Sparkles, CheckCircle, BarChart3, FileText } from 'lucide-react';

const OrderDisplay = ({
  listening,
  transcript,
  orderData,
  editingOrder,
  editedItems,
  menuItems,
  startOrderEditing,
  updateItemQuantity,
  removeItemFromOrder,
  saveOrderChanges,
  cancelOrderEditing
}) => {
  return (
    <div className="grid xl:grid-cols-2 gap-8">
      {/* Transcript Section */}
      <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 shadow-2xl">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
            <Volume2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Customer Order</h2>
            <p className="text-gray-400 text-sm">Real-time voice order capture</p>
          </div>
          {listening && (
            <div className="ml-auto flex items-center gap-3 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full">
              <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
              <span className="text-red-300 text-sm font-semibold uppercase tracking-wider">Live</span>
            </div>
          )}
        </div>
        <div className="bg-black/20 backdrop-blur rounded-2xl p-6 min-h-80 max-h-96 overflow-y-auto border border-white/5">
          <div className="text-gray-200 whitespace-pre-wrap leading-relaxed text-lg">
            {transcript || (
              <div className="text-gray-500 italic text-center py-16 space-y-4">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full flex items-center justify-center mx-auto opacity-50">
                  <Volume2 className="w-8 h-8 text-white" />
                </div>
                <p>Click "Take Order" to begin capturing customer order...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Analysis Section */}
      <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 shadow-2xl">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Order Analysis</h2>
            <p className="text-gray-400 text-sm">AI-powered order processing</p>
          </div>
          {orderData && !editingOrder && (
            <button
              onClick={startOrderEditing}
              className="ml-auto group flex items-center gap-2 px-4 py-2 rounded-xl font-semibold bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white transition-all transform hover:scale-105 active:scale-95 shadow-lg shadow-blue-500/25"
            >
              <div className="p-1 rounded-full bg-white/20">
                <CheckCircle className="w-4 h-4" />
              </div>
              Edit Order
            </button>
          )}
        </div>
        
        {!orderData ? (
          <div className="bg-black/20 backdrop-blur rounded-2xl p-8 min-h-80 flex items-center justify-center border border-white/5">
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center mx-auto opacity-50">
                <BarChart3 className="w-10 h-10 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-300 mb-2">Ready to Process</h3>
                <p className="text-gray-500">Take a customer order and hit process to see the breakdown</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6 max-h-96 overflow-y-auto">
            {/* Order Summary */}
            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 backdrop-blur rounded-2xl p-6 border border-blue-500/20">
              <h3 className="text-xl font-bold text-cyan-300 mb-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  📄
                </div>
                Order Summary
              </h3>
              <p className="text-gray-200 leading-relaxed text-lg">{orderData.order_summary}</p>
            </div>

            {/* Items - Only show ordered items */}
            {(editingOrder ? editedItems : orderData.items)?.length > 0 && (
              <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 backdrop-blur rounded-2xl p-6 border border-green-500/20">
                <h3 className="text-xl font-bold text-green-300 mb-4 flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  Ordered Items
                </h3>
                <div className="space-y-3">
                  {(editingOrder ? editedItems : orderData.items).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-4 bg-black/20 backdrop-blur rounded-xl border border-green-500/10 hover:border-green-500/30 transition-colors">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                        <span className="text-white text-sm font-bold">{idx + 1}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-4">
                          <span className="text-gray-200 leading-relaxed text-lg">
                            {editingOrder ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => updateItemQuantity(idx, parseInt(e.target.value) || 1)}
                                  className="w-16 bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-white text-center"
                                />
                                <span>x {menuItems[item.item]?.name || item.item}</span>
                              </div>
                            ) : (
                              `${item.quantity}x ${menuItems[item.item]?.name || item.item}`
                            )}
                          </span>
                          <span className="text-green-400 font-bold">${item.price.toFixed(2)}</span>
                        </div>
                        {item.customizations?.length > 0 && (
                          <div className="text-gray-400 text-sm mt-1">
                            ({item.customizations.join(', ')})
                          </div>
                        )}
                      </div>
                      {editingOrder && (
                        <button
                          onClick={() => removeItemFromOrder(idx)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur rounded-2xl p-6 border border-purple-500/20">
              <h3 className="text-xl font-bold text-purple-300 mb-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  💵
                </div>
                Total
              </h3>
              <p className="text-gray-200 leading-relaxed text-lg">
                ${editingOrder ? editedItems.reduce((sum, item) => sum + item.price, 0).toFixed(2) : orderData.total?.toFixed(2) || '0.00'}
              </p>
            </div>

            {/* Special Instructions */}
            {orderData.special_instructions && (
              <div className="bg-gradient-to-r from-pink-500/10 to-red-500/10 backdrop-blur rounded-2xl p-6 border border-pink-500/20">
                <h3 className="text-xl font-bold text-pink-300 mb-4 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-pink-400" />
                  Special Instructions
                </h3>
                <p className="text-gray-200 leading-relaxed text-lg">{orderData.special_instructions}</p>
              </div>
            )}

            {/* Edit Order Actions */}
            {editingOrder && (
              <div className="flex gap-4">
                <button
                  onClick={saveOrderChanges}
                  className="flex-1 group flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-semibold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white transition-all transform hover:scale-105 active:scale-95 shadow-lg shadow-green-500/25"
                >
                  <CheckCircle className="w-5 h-5" />
                  Save Changes
                </button>
                <button
                  onClick={cancelOrderEditing}
                  className="flex-1 group flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-semibold bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white transition-all transform hover:scale-105 active:scale-95 shadow-lg"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDisplay; 